import { cancel, select, takeEvery } from 'redux-saga/effects';
import { getProduktlinienDruck, mapLinienpreis } from '@ps20-frontend/druckdaten-lib';
import {
  DruckRequestPayload,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { Feature, GegeDruckdaten, GegenstandCategory, SpartenType } from '@ps20/bff-models';
import {
  App,
  mapLeistungen,
  mapToBffVorvertragsdatenDruck,
  numberToGermanNumberFormattedString,
} from '@ps20-frontend/frontend-models';
import {
  getSelectedProduktlinie,
  mapGesamtbeitragIfJaehrlichAndBeitragIsWrong,
} from '@ps20-frontend/preis-lib';
import { PS20Logger } from '@ps20-frontend/logging';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { IAppState } from '../appState';

export function* watchDruckdatenChanged() {
  yield takeEvery(GlobalActions.DRUCKDATEN_REQUESTED, safeSaga(onDruckdatenRequested, App.GEGE));
}

export function* onDruckdatenRequested(action: GlobalAction<DruckRequestPayload>) {
  try {
    const state: IAppState = yield select();
    const appIds: string[] = action.payload.appIds;

    if (!appIds.includes(App.GEGE)) {
      yield cancel();
      return;
    }

    const gegeDruckdaten: GegeDruckdaten = mapDruckdaten(state);
    GlobalPut.getInstance().put(new GlobalActions().druckdatenGenerated(gegeDruckdaten));
  } catch (e) {
    PS20Logger.error(App.GEGE, e as Error, 'Druckdatensaga.onDruckdatenRequested()');
    yield cancel();
  }
}

function mapDruckdaten(state: IAppState): GegeDruckdaten {
  const ausgewaehlteProduktlinie = getSelectedProduktlinie(state.produkt);
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  return {
    angebotsId: state.angebotsId,
    antragsnummer: state.antragsnummer,
    longText: ausgewaehlteProduktlinie?.langtext,
    shortText: ausgewaehlteProduktlinie?.kurztext,
    rbdAktuell: state.taasInfo.rbdNachlass,
    treuebonus: state.taasInfo.tbNachlass,
    vorteilsRabatt: vorteilsRabattActivated ? state.taasInfo.vorteilsrabatt || 0 : undefined,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    zaehlstuecke: 0,
    spartenType: SpartenType.GEGENSTANDSSCHUTZ,
    beginn: null, // kommt vom Checkout
    ablauf: null, // kommt vom Checkout
    gesamtbeitrag: numberToGermanNumberFormattedString(
      mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.GEGE,
      ),
    ),
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    vertragsdauer: null,
    linienpreis: mapLinienpreis(state.produkt),
    leistungen: mapLeistungen(ausgewaehlteProduktlinie, SpartenType.GEGENSTANDSSCHUTZ),
    zusatzoptionenDruck: [],
    vorversicherungenDruck: null,
    produktlinien: getProduktlinienDruck(state.produkt),
    zuersGefaehrdungsklasse: '',
    vorvertragsnummern: mapToBffVorvertragsdatenDruck(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    abweichungVomZielmarkt: false,
    beibehalten: state.vertragsoptionen.beibehalten,
    gegenstaende: null,
    gegenstandZusammenfassung: null,
    kooperation: state.vertragsoptionen.selectedKooperation,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
    vorschaedenDruck: state.gegenstandsschutz.items.some(item => item.category?.id === GegenstandCategory.FAHRRAD)
      ? { gegeFahrrad: state.fahrradVorschaeden?.count, vorhanden: state.fahrradVorschaeden?.provided ?? false }
      : null,
  };
}
